import { gql } from '@apollo/client';

import { Log, LogSessionContentRow } from '@/shared/types/analytics';

export interface LogSessionsPreview {
  rows: Log[];
}

export type LogSessionContent = { rows: LogSessionContentRow[] };

export interface LogSessionsCount {
  rows: { total_count: number }[];
}

export const SESSIONS_PREVIEW = gql`
  query ListSessions(
    $accountId: uuid
    $agentIds: _text
    $brainIds: _uuid
    $channels: _text
    $channelUserId: String
    $deskIds: _uuid
    $endDate: timestamp
    $externalUserId: String
    $hasCollection: Boolean
    $hasRecording: Boolean
    $hasVoicemail: Boolean
    $integrationIds: _uuid
    $isContained: Boolean
    $isCovered: Boolean
    $isMeaningful: Boolean
    $isTest: Boolean
    $isProactive: Boolean
    $languageModelIds: _text
    $minNumUserMessages: Int
    $ratings: _int4
    $sessionId: String
    $startDate: timestamp
    $tags: _text
    $tagsMatch: String
    $userEmail: String
    $userId: String
    $userName: String
    $limit: Int
    $offset: Int
    $min_num_thumbs_up: Int
    $min_num_thumbs_down: Int
    $csatBrain: _int4
    $goalAchieved: _int4
    $userSentiment: _int4
    $insightFilters: jsonb
  ) {
    rows: list_sessions(
      args: {
        account_id: $accountId
        agent_ids: $agentIds
        brain_parent_ids: $brainIds
        channels: $channels
        channel_user_id: $channelUserId
        csat_brain: $csatBrain
        goal_achieved: $goalAchieved
        user_sentiment: $userSentiment
        desk_ids: $deskIds
        end_time: $endDate
        external_user_id: $externalUserId
        has_collection: $hasCollection
        has_recording: $hasRecording
        has_voicemail: $hasVoicemail
        integration_ids: $integrationIds
        is_contained: $isContained
        is_covered: $isCovered
        is_meaningful: $isMeaningful
        is_test: $isTest
        is_proactive: $isProactive
        language_model_ids: $languageModelIds
        min_num_user_messages: $minNumUserMessages
        ratings: $ratings
        session_id: $sessionId
        start_time: $startDate
        tags: $tags
        tags_match: $tagsMatch
        user_email: $userEmail
        user_id: $userId
        user_name: $userName
        min_num_thumbs_up: $min_num_thumbs_up
        min_num_thumbs_down: $min_num_thumbs_down
        insight_filters: $insightFilters
        row_limit: $limit
        row_offset: $offset
      }
    ) {
      channel
      desk_id
      end_time
      is_contained
      is_covered
      is_test
      participated_agents
      participated_brains
      participated_language_models
      rating
      session_id
      start_time
      tags
      thumbs_down
      thumbs_up
      total_user_messages
      user_id
      user_name
      csat_brain
      csat_human_agent
      goal_achieved
      user_sentiment
      conversation_summary
      insights
    }
  }
`;

const SESSION_CONTENT_FIELDS = `
  brain_id
  brain_parent_id
  brain_version
  channel
  channel_user_id
  conversation_summary
  csat_brain
  csat_human_agent
  desk_id
  end_time
  expired_time
  external_user_id
  feedback
  goal_achieved
  insights
  integration_id
  is_contained
  is_covered
  is_test
  messages
  participated_agents
  participated_brains
  participated_collections
  rating
  rule_ids
  session_id
  start_time
  tags
  total_user_messages
  user_email
  user_id
  user_name
  user_sentiment
`;

export const SESSION_CONTENT = gql`
  query GetSessionContent($sessionId: String) {
    rows: get_session(args: { session_id: $sessionId }) {
      ${SESSION_CONTENT_FIELDS}
    }
  }
`;

export const SESSION_CONTENT_BOUNDED = gql`
  query GetSessionContentBounded($sessionId: String, $startDate: timestamp) {
    rows: get_session(
      args: { session_id: $sessionId, start_time: $startDate }
    ) {
      ${SESSION_CONTENT_FIELDS}
    }
  }
`;

export const SESSIONS_COUNT = gql`
  query ListSessionsCount(
    $accountId: uuid
    $agentIds: _text
    $brainIds: _uuid
    $channels: _text
    $channelUserId: String
    $deskIds: _uuid
    $endDate: timestamp
    $externalUserId: String
    $hasCollection: Boolean
    $hasRecording: Boolean
    $hasVoicemail: Boolean
    $integrationIds: _uuid
    $isContained: Boolean
    $isCovered: Boolean
    $isMeaningful: Boolean
    $isTest: Boolean
    $isProactive: Boolean
    $languageModelIds: _text
    $minNumUserMessages: Int
    $ratings: _int4
    $sessionId: String
    $startDate: timestamp
    $tags: _text
    $tagsMatch: String
    $userEmail: String
    $userId: String
    $userName: String
    $min_num_thumbs_up: Int
    $min_num_thumbs_down: Int
    $csatBrain: _int4
    $goalAchieved: _int4
    $userSentiment: _int4
    $insightFilters: jsonb
  ) {
    rows: list_sessions_count(
      args: {
        account_id: $accountId
        agent_ids: $agentIds
        brain_parent_ids: $brainIds
        channels: $channels
        channel_user_id: $channelUserId
        desk_ids: $deskIds
        end_time: $endDate
        external_user_id: $externalUserId
        has_collection: $hasCollection
        has_recording: $hasRecording
        has_voicemail: $hasVoicemail
        integration_ids: $integrationIds
        is_contained: $isContained
        is_covered: $isCovered
        is_meaningful: $isMeaningful
        is_test: $isTest
        is_proactive: $isProactive
        language_model_ids: $languageModelIds
        min_num_user_messages: $minNumUserMessages
        ratings: $ratings
        session_id: $sessionId
        start_time: $startDate
        tags: $tags
        tags_match: $tagsMatch
        user_email: $userEmail
        user_id: $userId
        user_name: $userName
        min_num_thumbs_up: $min_num_thumbs_up
        min_num_thumbs_down: $min_num_thumbs_down
        csat_brain: $csatBrain
        goal_achieved: $goalAchieved
        user_sentiment: $userSentiment
        insight_filters: $insightFilters
      }
    ) {
      total_count
    }
  }
`;
